import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { useToast } from "../../utils/toast";
import AdminService from "../../services/admin/admin.services";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { ReportDto } from "../../services/admin/admin.dto";
import { useFormik } from "formik";
import { ConfirmPopup } from "primereact/confirmpopup";
import { confirmPopup } from "primereact/confirmpopup";

const EditReportsComponent = () => {
  const toast = useToast();
  const [reports, setReports] = useState<ReportDto[]>([]);
  const [displayReportDialog, setDisplayReportDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const categoryOptions = [
    { label: "İç Rapor", value: "İç Rapor" },
    { label: "Dış Rapor", value: "Dış Rapor" }
  ];

  const formik = useFormik<{ id?: number; title: string; date: Date; category: string }>({
    initialValues: {
      title: "",
      date: new Date(),
      category: ""
    },
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.title) {
        errors.title = "Başlık zorunludur";
      }
      if (!values.date) {
        errors.date = "Tarih zorunludur";
      }
      if (!isEditMode && !values.category) {
        errors.category = "Kategori zorunludur";
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        if (isEditMode) {
          // Update existing report
          if (!formik.values.id) {
            toast.show("Rapor ID'si bulunamadı.", "error");
            return;
          }
          const response = await AdminService.updateReport(formik.values.id, {
            title: values.title,
            date: values.date.toISOString().split("T")[0],
            category: values.category
          });
          if (response && response.result?.statusCode === 200) {
            toast.show("Rapor başarıyla güncellendi.", "success");
            fetchReports();
            setDisplayReportDialog(false);
            formik.resetForm();
          } else {
            const errorMessage =
              typeof response?.error === "string" ? response.error : "Rapor güncellenirken bir hata oluştu.";
            toast.show(errorMessage, "error");
          }
        } else {
          // Create new report
          if (!selectedFile) {
            toast.show("Lütfen bir dosya seçin.", "error");
            return;
          }

          const formData = new FormData();
          formData.append("link", selectedFile);
          formData.append("title", values.title);
          formData.append("date", values.date.toISOString().split("T")[0]);
          formData.append("category", values.category);

          const response = await AdminService.createReport(formData as any);
          if (response && response.result?.statusCode === 201) {
            toast.show("Rapor başarıyla yüklendi.", "success");
            fetchReports();
            setDisplayReportDialog(false);
            formik.resetForm();
            setSelectedFile(null);
          } else {
            const errorMessage =
              typeof response?.result?.error === "string"
                ? response.result.error
                : "Rapor yüklenirken bir hata oluştu.";
            toast.show(errorMessage, "error");
          }
        }
      } catch (error) {
        console.error("Error:", error);
        toast.show("İşlem sırasında bir hata oluştu.", "error");
      }
    }
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await AdminService.getReports();
      if (response && response.result) {
        setReports(response.result.data);
      } else {
        toast.show("Raporlar yüklenirken hata oluştu.", "error");
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.show("Raporlar yüklenirken hata oluştu.", "error");
    }
  };

  const handleReportDelete = async (id: number, title: string, event: React.MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: event.currentTarget,
      message: `"${title}" başlıklı videoyu silmek istediğinize emin misiniz?`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      accept: async () => {
        try {
          const response = await AdminService.deleteReport(id);
          if (response?.result?.statusCode === 200) {
            toast.show(response?.result?.data || "Rapor başarıyla silindi", "success");
            fetchReports();
          } else {
            toast.show(response?.result?.error || "Rapor silinirken bir hata oluştu.", "error");
          }
        } catch (error) {
          console.error("Error deleting report:", error);
          toast.show("Rapor silinirken bir hata oluştu.", "error");
        }
      },
      reject: () => {}
    });
  };

  const handleFileSelect = (event: any) => {
    const file = event.files[0];
    setSelectedFile(file);
  };

  const reportActionBodyTemplate = (rowData: ReportDto) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="mr-2"
          onClick={() => {
            setIsEditMode(true);
            formik.setValues({
              id: rowData.id,
              title: rowData.title,
              date: new Date(rowData.date),
              category: rowData.category
            });
            setDisplayReportDialog(true);
          }}
        />
        <Button
          icon="pi pi-download"
          rounded
          outlined
          severity="info"
          className="mr-2"
          onClick={async () => {
            try {
              const response = await AdminService.downloadReport(rowData.id!);

              if (!response || !response.result) {
                throw new Error("Download failed");
              }

              // Assert response.result as unknown, then as Blob
              const blob = response.result as unknown as Blob;

              // Try to get the filename from the Content-Disposition header
              const contentDisposition = response.headers?.["content-disposition"];
              let filename = rowData.title;
              if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (filenameMatch) {
                  filename = filenameMatch[1];
                }
              } else {
                // If no Content-Disposition header, get the extension from the link URL
                const extension = rowData.link.split(".").pop();
                filename = `${rowData.title}.${extension}`;
              }

              // Create object URL from the blob
              const url = window.URL.createObjectURL(blob);

              // Create temporary link and trigger download
              const link = document.createElement("a");
              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();

              // Cleanup
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error("Download error:", error);
              toast.show("Dosya indirilirken bir hata oluştu.", "error");
            }
          }}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={(e) => handleReportDelete(rowData.id!, rowData.title, e)}
        />
      </div>
    );
  };

  const renderReportTable = () => {
    return (
      <>
        <ConfirmPopup />
        <div className="flex justify-content-end mb-3">
          <Button
            label="Yeni Rapor Ekle"
            icon="pi pi-plus"
            onClick={() => {
              setIsEditMode(false);
              formik.resetForm();
              setSelectedFile(null);
              setDisplayReportDialog(true);
            }}
          />
        </div>

        <DataTable value={reports} paginator rows={10} dataKey="id">
          <Column field="title" header="Başlık" sortable />
          <Column
            field="date"
            header="Tarih"
            sortable
            body={(rowData) => {
              const date = new Date(rowData.date);
              return date.toLocaleDateString("tr-TR", { year: "numeric", month: "long" });
            }}
          />
          <Column field="category" header="Kategori" />
          <Column body={reportActionBodyTemplate} exportable={false} style={{ minWidth: "12rem" }} />
        </DataTable>
      </>
    );
  };

  return (
    <div className="flex flex-column justify-content-center p-0">
      {renderReportTable()}

      <Dialog
        visible={displayReportDialog}
        style={{ width: "50vw" }}
        header={isEditMode ? "Rapor Düzenle" : "Yeni Rapor"}
        modal
        className="p-fluid"
        onHide={() => setDisplayReportDialog(false)}
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="field">
            <label htmlFor="title">Başlık</label>
            <InputText
              id="title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              className={formik.errors.title ? "p-invalid" : ""}
              maxLength={200}
            />
            {formik.errors.title && <small className="p-error">{formik.errors.title}</small>}
          </div>

          <div className="field">
            <label htmlFor="date">Tarih</label>
            <Calendar
              id="date"
              name="date"
              value={formik.values.date}
              onChange={(e) => formik.setFieldValue("date", e.value)}
              view="month"
              dateFormat="mm/yy"
              className={formik.errors.date ? "p-invalid" : ""}
            />
            {formik.errors.date && <small className="p-error">{String(formik.errors.date)}</small>}
          </div>

          <div className="field">
            <label htmlFor="category">Kategori</label>
            <Dropdown
              id="category"
              name="category"
              value={formik.values.category}
              options={categoryOptions}
              onChange={(e) => formik.setFieldValue("category", e.value)}
              placeholder="Kategori Seçiniz"
              className={formik.errors.category ? "p-invalid" : ""}
            />
            {formik.errors.category && <small className="p-error">{formik.errors.category}</small>}
          </div>
          {!isEditMode && (
            <>
              <div className="field">
                <label htmlFor="file_upload">Dosya Yükle</label>
                <FileUpload
                  id="file_upload"
                  name="demo[]"
                  customUpload
                  uploadHandler={handleFileSelect}
                  auto
                  chooseLabel="Dosya Seç"
                  mode="basic"
                />
                {selectedFile && <small className="p-d-block">Seçilen Dosya: {selectedFile.name}</small>}
              </div>
            </>
          )}

          <div className="flex justify-content-end mt-4">
            <Button
              label="İptal"
              icon="pi pi-times"
              outlined
              onClick={() => setDisplayReportDialog(false)}
              className="mr-2"
              type="button"
            />
            <Button label="Kaydet" icon="pi pi-check" type="submit" disabled={formik.isSubmitting} />
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default EditReportsComponent;
